import type { Script, Beat } from '../script/schema';

export type Locale = 'en' | 'zh';

export interface GameState {
  scriptId: string;
  beatId: string;
  flags: Record<string, boolean | string | number>;
  history: string[];
  locale?: Locale;
  latestBeatId?: string;       // high-water mark: furthest beat ever reached
  visitedSceneIds?: string[];  // scene beat IDs crossed, in order
}

/** A choice option paired with its original index in the beat's options array. */
export interface IndexedOption {
  index: number;
  label: string;
  goto: string;
  setFlag?: Record<string, boolean | string | number>;
}

const MAX_HISTORY = 200;

export class Game {
  state: GameState;

  constructor(public script: Script) {
    if (script.beats.length === 0) {
      throw new Error('Script has no beats');
    }
    const firstBeat = script.beats[0];
    this.state = {
      scriptId: script.meta.id,
      beatId: firstBeat.id,
      flags: {},
      history: [firstBeat.id],
      locale: 'en',
      latestBeatId: firstBeat.id,
      visitedSceneIds: firstBeat.kind === 'scene' ? [firstBeat.id] : [],
    };
  }

  /** Set the active locale. */
  setLocale(locale: Locale): void {
    this.state.locale = locale;
  }

  /** Return the current beat. */
  current(): Beat {
    const beat = this.script.beats.find((b) => b.id === this.state.beatId);
    if (!beat) {
      throw new Error(`Beat not found: ${this.state.beatId}`);
    }
    return beat;
  }

  /**
   * Return the current beat with locale overrides applied.
   * When locale is 'en' or no override exists, returns the original beat.
   * For choice beats, merges option labels from the locale's options string array.
   */
  getCurrentBeatLocalized(): Beat {
    const beat = this.current();
    const locale = this.state.locale ?? 'en';

    // English is always the base — no overlay needed
    if (locale === 'en') return beat;

    const override = this.script.locales?.[locale]?.[beat.id];
    if (!override) return beat;

    if (beat.kind === 'say') {
      return {
        ...beat,
        ...(override.text !== undefined ? { text: override.text } : {}),
        ...(override.speaker !== undefined ? { speaker: override.speaker } : {}),
      };
    }

    if (beat.kind === 'choice') {
      const localizedOptions = override.options
        ? beat.options.map((opt, i) => {
            const localizedLabel = override.options?.[i];
            return localizedLabel !== undefined ? { ...opt, label: localizedLabel } : opt;
          })
        : beat.options;
      return {
        ...beat,
        ...(override.prompt !== undefined ? { prompt: override.prompt } : {}),
        options: localizedOptions,
      };
    }

    // For other beat kinds, apply any matching fields
    return { ...beat } as Beat;
  }

  /**
   * Advance to the next beat in sequence.
   * Returns the next beat, or null if we've reached the end of the array
   * (should not normally happen — scripts end with an 'end' beat).
   */
  advance(): Beat | null {
    const idx = this.script.beats.findIndex(
      (b) => b.id === this.state.beatId,
    );
    if (idx === -1) {
      throw new Error(`Current beat not found: ${this.state.beatId}`);
    }
    const nextIdx = idx + 1;
    if (nextIdx >= this.script.beats.length) {
      return null;
    }
    const next = this.script.beats[nextIdx];
    this.pushBeat(next.id);
    return next;
  }

  /** Jump to a specific beat by id. */
  jump(beatId: string): Beat {
    const beat = this.script.beats.find((b) => b.id === beatId);
    if (!beat) {
      throw new Error(`Unknown beat id: ${beatId}`);
    }
    this.pushBeat(beat.id);
    return beat;
  }

  /** Set a flag value. */
  setFlag(flag: string, value: boolean | string | number): void {
    this.state.flags[flag] = value;
  }

  /** Get the value of a flag, or undefined if not set. */
  getFlag(flag: string): boolean | string | number | undefined {
    return this.state.flags[flag];
  }

  /**
   * Evaluate a flag condition against the current game state.
   * Returns true if the condition is met (or if no condition is provided).
   */
  evaluateCondition(condition?: {
    flag: string;
    value: boolean | string | number;
    op?: 'eq' | 'neq';
  }): boolean {
    if (!condition) return true;
    const flagValue = this.state.flags[condition.flag];
    const op = condition.op ?? 'eq';
    if (op === 'eq') return flagValue === condition.value;
    return flagValue !== condition.value;
  }

  /**
   * Return the visible options for the current choice beat,
   * filtered by their conditions against the current flag state.
   * Each entry includes the original index for use with applyChoice().
   */
  getVisibleOptions(): IndexedOption[] {
    const beat = this.current();
    if (beat.kind !== 'choice') {
      throw new Error(`Current beat is not a choice: ${beat.kind}`);
    }
    const visible: IndexedOption[] = [];
    for (let i = 0; i < beat.options.length; i++) {
      const opt = beat.options[i];
      if (this.evaluateCondition(opt.condition)) {
        visible.push({
          index: i,
          label: opt.label,
          goto: opt.goto,
          setFlag: opt.setFlag,
        });
      }
    }
    return visible;
  }

  /**
   * Apply a choice option by index (original index in the beat's options array).
   * Sets any flags from the option and jumps to its goto target.
   */
  applyChoice(optionIndex: number): Beat {
    const beat = this.current();
    if (beat.kind !== 'choice') {
      throw new Error(`Current beat is not a choice: ${beat.kind}`);
    }
    const option = beat.options[optionIndex];
    if (!option) {
      throw new Error(`Invalid option index: ${optionIndex}`);
    }
    if (option.setFlag) {
      for (const [key, val] of Object.entries(option.setFlag)) {
        this.setFlag(key, val);
      }
    }
    return this.jump(option.goto);
  }

  /**
   * Restore the game to a previously saved state.
   * The state's scriptId must match the current script.
   */
  restoreState(saved: GameState): void {
    if (saved.scriptId !== this.script.meta.id) {
      throw new Error(
        `Script mismatch: save is for "${saved.scriptId}" but loaded script is "${this.script.meta.id}"`,
      );
    }
    // Verify the beat exists in the current script
    const beat = this.script.beats.find((b) => b.id === saved.beatId);
    if (!beat) {
      throw new Error(`Saved beat not found in script: ${saved.beatId}`);
    }
    this.state = {
      scriptId: saved.scriptId,
      beatId: saved.beatId,
      flags: { ...saved.flags },
      history: [...saved.history],
      locale: saved.locale ?? 'en',
      latestBeatId: saved.latestBeatId,
      visitedSceneIds: saved.visitedSceneIds ? [...saved.visitedSceneIds] : [],
    };
  }

  /**
   * Go back one say-beat in history, within the current scene.
   * Returns the beat to display, or null if we're already at the
   * start of the scene (first say-beat after the most recent 'scene' beat)
   * or at a choice boundary.
   *
   * Constraints:
   * - Never crosses a 'scene' beat boundary (scene-local only)
   * - Never crosses a 'choice' beat (choices are irreversible)
   * - Only targets 'say' beats (skips non-displayable beats)
   */
  goBack(): Beat | null {
    const history = this.state.history;
    if (history.length < 2) return null;

    // Walk backwards from the second-to-last entry, looking for the previous
    // say beat within the same scene.
    // We need to find the most recent 'scene' beat in the full script up to
    // and including the current position, to know our scene boundary.
    const currentIdx = this.script.beats.findIndex((b) => b.id === this.state.beatId);
    if (currentIdx <= 0) return null;

    // Find the scene boundary: the last 'scene' or 'choice' beat at or before current
    let sceneBoundaryIdx = -1;
    for (let i = currentIdx - 1; i >= 0; i--) {
      const b = this.script.beats[i];
      if (b.kind === 'scene' || b.kind === 'choice') {
        sceneBoundaryIdx = i;
        break;
      }
    }

    // Walk backwards in history (skip the current beat itself)
    for (let h = history.length - 2; h >= 0; h--) {
      const beatId = history[h];
      const beat = this.script.beats.find((b) => b.id === beatId);
      if (!beat) continue;

      // Stop at choice or scene boundaries
      if (beat.kind === 'choice' || beat.kind === 'scene') return null;

      // Only go back to say beats (displayable)
      if (beat.kind !== 'say') continue;

      // Ensure this beat is within the current scene (after the scene boundary)
      const beatIdx = this.script.beats.findIndex((b) => b.id === beatId);
      if (beatIdx <= sceneBoundaryIdx) return null;

      // Valid target — rewind history to this point and move there
      this.state.history = history.slice(0, h + 1);
      this.state.beatId = beatId;
      return beat;
    }

    return null;
  }

  /**
   * Returns true if goBack() would succeed (used to enable/disable the Back button).
   */
  canGoBack(): boolean {
    const history = this.state.history;
    if (history.length < 2) return false;

    const currentIdx = this.script.beats.findIndex((b) => b.id === this.state.beatId);
    if (currentIdx <= 0) return false;

    let sceneBoundaryIdx = -1;
    for (let i = currentIdx - 1; i >= 0; i--) {
      const b = this.script.beats[i];
      if (b.kind === 'scene' || b.kind === 'choice') {
        sceneBoundaryIdx = i;
        break;
      }
    }

    for (let h = history.length - 2; h >= 0; h--) {
      const beatId = history[h];
      const beat = this.script.beats.find((b) => b.id === beatId);
      if (!beat) continue;
      if (beat.kind === 'choice' || beat.kind === 'scene') return false;
      if (beat.kind !== 'say') continue;
      const beatIdx = this.script.beats.findIndex((b) => b.id === beatId);
      if (beatIdx <= sceneBoundaryIdx) return false;
      return true;
    }

    return false;
  }

  private pushBeat(id: string): void {
    this.state.beatId = id;
    this.state.history.push(id);
    if (this.state.history.length > MAX_HISTORY) {
      this.state.history = this.state.history.slice(-MAX_HISTORY);
    }
    // High-water mark: update if this beat is further in the script
    const currentIdx = this.script.beats.findIndex((b) => b.id === id);
    const latestIdx = this.state.latestBeatId
      ? this.script.beats.findIndex((b) => b.id === this.state.latestBeatId)
      : -1;
    if (currentIdx > latestIdx) {
      this.state.latestBeatId = id;
    }
    // Track visited scenes
    const beat = this.script.beats.find((b) => b.id === id);
    if (beat?.kind === 'scene') {
      if (!this.state.visitedSceneIds) this.state.visitedSceneIds = [];
      if (!this.state.visitedSceneIds.includes(id)) {
        this.state.visitedSceneIds.push(id);
      }
    }
  }

  /** Returns true if the player has jumped back — latestBeatId is ahead of current beatId. */
  isAheadOfCurrent(): boolean {
    if (!this.state.latestBeatId) return false;
    if (this.state.latestBeatId === this.state.beatId) return false;
    const currentIdx = this.script.beats.findIndex((b) => b.id === this.state.beatId);
    const latestIdx = this.script.beats.findIndex((b) => b.id === this.state.latestBeatId);
    return latestIdx > currentIdx;
  }
}
